import axiosInstance from "@helper/interceptor";

export const getAllSensors = async (params = {}) => {
  const {
    page = 1,
    pageSize = 100,
    search = "",
    project_id,
    without_project,
    unassigned,
    available_for_installation,
    sensor_type,
    ordering,
    area,
    sensor_state,
  } = params;
  let response = await axiosInstance.get("sensors/", {
    params: {
      page,
      page_size: pageSize,
      search,
      ...(project_id && { project_id }),
      ...(without_project && { without_project }),
      ...(unassigned && { unassigned }),
      ...(available_for_installation && { available_for_installation }),
      ...(sensor_type && { sensor_type }),
      ...(ordering && { ordering }),
      ...(area && { area }),
      ...(sensor_state && { sensor_state }),
    },
    timeout: 30000, // 30 second timeout for large datasets
  });
  return response.data;
};

export const getAllSensorsBatched = async () => {
  const pageSize = 1000;
  let first;
  try {
    first = await getAllSensors({ page: 1, pageSize });
  } catch (error) {
    console.warn("Failed to load sensors:", error);
    return [];
  }
  if (!first.results) return Array.isArray(first) ? first : [];
  const allSensors = [...first.results];
  if (!first.next) return allSensors;

  const effectivePageSize = first.results.length;
  const totalPages = Math.ceil(first.count / effectivePageSize);
  const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
  const CONCURRENCY = 3;

  for (let i = 0; i < remainingPages.length; i += CONCURRENCY) {
    const batch = remainingPages.slice(i, i + CONCURRENCY);
    const responses = await Promise.allSettled(
      batch.map((page) => getAllSensors({ page, pageSize: effectivePageSize }))
    );
    responses.forEach((result, idx) => {
      if (result.status === "fulfilled" && result.value.results)
        allSensors.push(...result.value.results);
      else if (result.status === "rejected")
        console.warn(`Failed to load sensors page ${batch[idx]}:`, result.reason);
    });
  }

  return allSensors;
};
export const getSensorsProject = async (id) => {
  let response = await axiosInstance.get(`project/${id}/sensors`);
  return response.data;
};
export const getSensor = async (id) => {
  let response = await axiosInstance.get(`sensors/${id}`);
  return response.data;
};

export const getLastSend = async (id) => {
  let response = await axiosInstance.get(`last-send/${id}`);
  return response.data;
};

export const getInfluxData = async (start, stop, interval, hardwareSerial) => {
  let response = await axiosInstance.get(
    `api/influx-data/?start=${start}&stop=${stop}&interval=${interval}&hardwareSerial=${hardwareSerial}`
  );
  return response.data;
};

export const createSensor = async (formData) => {
  const response = await axiosInstance.post("sensors/", formData);
  return response;
};

export const updateSensor = async ({ updateFormData, detail }) => {
  const response = await axiosInstance.put(`sensors/${detail.id}/`, updateFormData);
  return response;
};

// Partial update for single field changes (doesn't overwrite other fields)
export const patchSensor = async ({ id, data }) => {
  const response = await axiosInstance.patch(`sensors/${id}/`, data);
  return response;
};
export const deleteSensor = async (id) => {
  try {
    const response = await axiosInstance.delete(`sensors/${id}`);
    return response;
  } catch (error) {
    throw new Error({
      message: "Can't delete this sensor.There are some tree linked with this sensor",
    });
  }
};

export const sensorPlot = async (sensor) => {
  let type = "resistance";
  if (sensor.soil_moisture_sensor) {
    type = "water_soil";
  } else if (sensor.dendrometer_sensor) {
    type = "dendrometer";
  } else if (sensor.soil_water_tension_sensor) {
    type = "soil_water_tension";
  } else if (sensor.tilt_sensor) {
    type = "tilt";
  }
  const response = await axiosInstance.get(`ts_data/${sensor.id}-${type}`);
  return response.data;
};

export const installationPlot = async (installation) => {
  let type = "resistance";
  if (installation.sensor.soil_moisture_sensor) {
    type = "water_soil";
  } else if (installation.sensor.dendrometer_sensor) {
    type = "dendrometer";
  } else if (installation.sensor.soil_water_tension_sensor) {
    type = "soil_water_tension";
  } else if (installation.sensor.tilt_sensor) {
    type = "tilt";
  }
  let response = await axiosInstance.get(
    `data/${installation.tree.id}+${installation.sensor.id}-${type}`
  );
  response.data.x = response.data.x[installation.tree.id];
  response.data.y = response.data.y[installation.tree.id];
  return response.data;
};

export const refreshSensorHealth = async (sensorId) => {
  let response = await axiosInstance.post(`sensors/${sensorId}/refresh_health/`);
  return response.data;
};

/**
 * Send downlink configuration to NB-IoT sensor.
 * Queues TDC (send interval) and/or MESSION (measurement interval) commands.
 * @param {number} sensorId - The sensor ID
 * @returns {Promise<{message: string, imei: string, payloads: string[]}>}
 */
export const sendDownlink = async (sensorId) => {
  let response = await axiosInstance.post(`sensors/${sensorId}/send_downlink/`);
  return response.data;
};

export const calibrateTiltSensor = async (sensorId) => {
  let response = await axiosInstance.post(`sensors/${sensorId}/calibrate_tilt/`);
  return response.data;
};
